/**
 * Utility functions for handling GeoJSON data in Google Maps
 */

/**
 * Convert GeoJSON coordinates to Google Maps LatLng objects
 * @param {Array} coordinates - GeoJSON coordinates array
 * @param {string} type - Geometry type (Polygon, MultiPolygon, etc.)
 * @returns {Array} Array of Google Maps LatLng objects (for single polygon) or Array of Arrays (for multipolygon)
 */
export const geoJsonToGoogleMapsPath = (coordinates, type = 'Polygon') => {
  if (!coordinates || !Array.isArray(coordinates)) {
    return [];
  }

  switch (type) {
    case 'Polygon':
      // For Polygon, coordinates[0] is the exterior ring
      return coordinates[0].map(coord => ({
        lat: coord[1],
        lng: coord[0]
      }));
    
    case 'MultiPolygon':
      // For MultiPolygon, return array of polygons (each polygon is an array of LatLng objects)
      return coordinates.map(polygon => 
        polygon[0].map(coord => ({
          lat: coord[1],
          lng: coord[0]
        }))
      );
    
    case 'LineString':
      return coordinates.map(coord => ({
        lat: coord[1],
        lng: coord[0]
      }));
    
    default:
      console.warn(`Unsupported geometry type: ${type}`);
      return [];
  }
};

/**
 * Create a Google Maps bounds object from GeoJSON coordinates
 * @param {Array} coordinates - GeoJSON coordinates array
 * @param {string} type - Geometry type
 * @returns {Object} Google Maps bounds object
 */
export const createBoundsFromGeoJson = (coordinates, type = 'Polygon') => {
  const paths = geoJsonToGoogleMapsPath(coordinates, type);
  if (paths.length === 0) return null;

  const bounds = new window.google.maps.LatLngBounds();
  
  if (type === 'MultiPolygon') {
    // For MultiPolygon, paths is an array of arrays
    paths.forEach(polygon => {
      polygon.forEach(point => {
        bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
      });
    });
  } else {
    // For Polygon and LineString, paths is a single array
    paths.forEach(point => {
      bounds.extend(new window.google.maps.LatLng(point.lat, point.lng));
    });
  }
  
  return bounds;
};

/**
 * Calculate the center point of a GeoJSON geometry
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {Object} Center point with lat and lng
 */
export const calculateGeometryCenter = (geometry) => {
  if (!geometry || !geometry.coordinates) {
    return { lat: 0, lng: 0 };
  }

  const paths = geoJsonToGoogleMapsPath(geometry.coordinates, geometry.type);
  if (paths.length === 0) {
    return { lat: 0, lng: 0 };
  }

  let allPoints = [];
  
  if (geometry.type === 'MultiPolygon') {
    // For MultiPolygon, paths is an array of arrays
    paths.forEach(polygon => {
      allPoints = allPoints.concat(polygon);
    });
  } else {
    // For Polygon and LineString, paths is a single array
    allPoints = paths;
  }

  if (allPoints.length === 0) {
    return { lat: 0, lng: 0 };
  }

  // Calculate centroid
  const sum = allPoints.reduce((acc, point) => ({
    lat: acc.lat + point.lat,
    lng: acc.lng + point.lng
  }), { lat: 0, lng: 0 });

  return {
    lat: sum.lat / allPoints.length,
    lng: sum.lng / allPoints.length
  };
};

/**
 * Validate GeoJSON geometry
 * @param {Object} geometry - GeoJSON geometry object
 * @returns {boolean} True if valid
 */
export const validateGeoJsonGeometry = (geometry) => {
  if (!geometry || typeof geometry !== 'object') {
    return false;
  }

  if (!geometry.type || !geometry.coordinates) {
    return false;
  }

  const validTypes = ['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon'];
  if (!validTypes.includes(geometry.type)) {
    return false;
  }

  if (!Array.isArray(geometry.coordinates)) {
    return false;
  }

  return true;
};

export const extractPolygonCoordinateSets = (geometry) => {
  const polygonSets = [];

  const visit = (currentGeometry) => {
    if (!currentGeometry || typeof currentGeometry !== 'object') {
      return;
    }

    if (currentGeometry.type === 'Feature') {
      visit(currentGeometry.geometry);
      return;
    }

    if (currentGeometry.type === 'GeometryCollection') {
      if (Array.isArray(currentGeometry.geometries)) {
        currentGeometry.geometries.forEach(visit);
      }
      return;
    }

    if (!Array.isArray(currentGeometry.coordinates)) {
      return;
    }

    if (currentGeometry.type === 'Polygon') {
      if (currentGeometry.coordinates.length > 0) {
        polygonSets.push(currentGeometry.coordinates);
      }
      return;
    }

    if (currentGeometry.type === 'MultiPolygon') {
      currentGeometry.coordinates.forEach((polygonCoordinates) => {
        if (Array.isArray(polygonCoordinates) && polygonCoordinates.length > 0) {
          polygonSets.push(polygonCoordinates);
        }
      });
    }
  };

  visit(geometry);
  return polygonSets;
};

/**
 * Simplify GeoJSON coordinates for better performance
 * @param {Array} coordinates - Original coordinates
 * @param {number} tolerance - Simplification tolerance (0-1)
 * @returns {Array} Simplified coordinates
 */
export const simplifyGeoJsonCoordinates = (coordinates, tolerance = 0.01) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) {
    return coordinates;
  }

  // Simple distance-based simplification
  const simplified = [coordinates[0]]; // Always keep first point
  
  for (let i = 1; i < coordinates.length - 1; i++) {
    const prev = coordinates[i - 1];
    const curr = coordinates[i];
    const next = coordinates[i + 1];
    
    // Calculate distance between current point and line from prev to next
    const distance = pointToLineDistance(curr, prev, next);
    
    if (distance > tolerance) {
      simplified.push(curr);
    }
  }
  
  simplified.push(coordinates[coordinates.length - 1]); // Always keep last point
  return simplified;
};

/**
 * Calculate distance from point to line segment
 * @param {Array} point - [lng, lat]
 * @param {Array} lineStart - [lng, lat]
 * @param {Array} lineEnd - [lng, lat]
 * @returns {number} Distance
 */
const pointToLineDistance = (point, lineStart, lineEnd) => {
  const A = point[0] - lineStart[0];
  const B = point[1] - lineStart[1];
  const C = lineEnd[0] - lineStart[0];
  const D = lineEnd[1] - lineStart[1];

  const dot = A * C + B * D;
  const lenSq = C * C + D * D;
  
  if (lenSq === 0) return Math.sqrt(A * A + B * B);
  
  const param = dot / lenSq;
  
  let xx, yy;
  if (param < 0) {
    xx = lineStart[0];
    yy = lineStart[1];
  } else if (param > 1) {
    xx = lineEnd[0];
    yy = lineEnd[1];
  } else {
    xx = lineStart[0] + param * C;
    yy = lineStart[1] + param * D;
  }

  const dx = point[0] - xx;
  const dy = point[1] - yy;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Load and process GeoJSON data from a file
 * @param {string} filePath - Path to GeoJSON file
 * @returns {Promise<Object>} Processed GeoJSON data
 */
export const loadGeoJsonData = async (filePath) => {
  try {
    const response = await fetch(filePath);
    if (!response.ok) {
      throw new Error(`Failed to load GeoJSON: ${response.statusText}`);
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error loading GeoJSON data:', error);
    throw error;
  }
};

/**
 * Convert shapefile to GeoJSON using the shapefile package
 * @param {string} shapefilePath - Path to shapefile (.shp)
 * @returns {Promise<Object>} GeoJSON data
 */
export const convertShapefileToGeoJson = async (shapefilePath) => {
  try {
    const { open } = await import('shapefile');
    const source = await open(shapefilePath);
    const features = [];
    let result = await source.read();
    
    while (!result.done) {
      features.push(result.value);
      result = await source.read();
    }
    
    return {
      type: 'FeatureCollection',
      features: features
    };
  } catch (error) {
    console.error('Error converting shapefile to GeoJSON:', error);
    throw error;
  }
};
